import { Chart, Plugin } from 'chart.js'

const PILL_HEIGHT = 20
const PILL_RADIUS = 10
const FONT = '500 12px system-ui, -apple-system, sans-serif'
const PLAIN_FONT = '12px system-ui, -apple-system, sans-serif'

/**
 * Factory that creates a per-instance pill plugin.
 *
 * @param id        Unique plugin id (avoid collisions between chart instances)
 * @param label     Text inside the pill (e.g. "Hoje" or "Agora")
 * @param getIndex  Function that returns the X-axis index to place the pill on
 * @param alwaysPill  When true the pill bg is always drawn (for "Agora")
 */
export function makePillPlugin(
  id: string,
  label: string,
  getIndex: () => number,
  alwaysPill = false
): Plugin {
  return {
    id: `pill-${id}`,
    afterDraw(chart: Chart) {
      const { ctx, scales } = chart
      const xScale = scales['x']
      if (!xScale) return

      const index = getIndex()
      if (index < 0 || index >= xScale.ticks.length) return

      const x = xScale.getPixelForValue(index)
      const y = (xScale.top + xScale.bottom) / 2

      ctx.save()

      const isPill = alwaysPill || label === 'Hoje' || label === 'Agora'

      if (isPill) {
        ctx.font = FONT
        const textWidth = ctx.measureText(label).width
        const pillWidth = textWidth + 16
        const rawPillX = x - pillWidth / 2
        const pillX = Math.max(0, Math.min(rawPillX, chart.width - pillWidth))
        const pillY = y - PILL_HEIGHT / 2

        // Draw rounded rect
        ctx.beginPath()
        ctx.moveTo(pillX + PILL_RADIUS, pillY)
        ctx.lineTo(pillX + pillWidth - PILL_RADIUS, pillY)
        ctx.arcTo(
          pillX + pillWidth,
          pillY,
          pillX + pillWidth,
          pillY + PILL_HEIGHT,
          PILL_RADIUS
        )
        ctx.lineTo(pillX + pillWidth, pillY + PILL_HEIGHT - PILL_RADIUS)
        ctx.arcTo(
          pillX + pillWidth,
          pillY + PILL_HEIGHT,
          pillX,
          pillY + PILL_HEIGHT,
          PILL_RADIUS
        )
        ctx.lineTo(pillX + PILL_RADIUS, pillY + PILL_HEIGHT)
        ctx.arcTo(pillX, pillY + PILL_HEIGHT, pillX, pillY, PILL_RADIUS)
        ctx.lineTo(pillX, pillY + PILL_RADIUS)
        ctx.arcTo(pillX, pillY, pillX + pillWidth, pillY, PILL_RADIUS)
        ctx.closePath()

        ctx.fillStyle = '#f1f8fd'
        ctx.fill()

        ctx.fillStyle = '#0366dd'
        ctx.font = FONT
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(label, pillX + pillWidth / 2, y)
      } else {
        ctx.fillStyle = '#707070'
        ctx.font = PLAIN_FONT
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(label, x, y)
      }

      ctx.restore()
    }
  }
}
